import { AnimationSettings, Keyframe } from '../types/design';

/**
 * Interpolates between two values based on a progress [0, 1]
 */
const lerp = (start: number, end: number, progress: number) => {
  return start + (end - start) * progress;
};

/**
 * Gets the value of a property at a specific time based on keyframes
 */
export const interpolateProperty = (
  time: number,
  // duration: number,
  keyframes: Keyframe[],
  propertyName: string,
  defaultValue: any
) => {
  if (!keyframes || keyframes.length === 0) return defaultValue;

  // Sort keyframes by time
  const sorted = [...keyframes].sort((a, b) => a.time - b.time);

  // If time is before first keyframe
  if (time <= sorted[0].time) return sorted[0].properties[propertyName] ?? defaultValue;

  // If time is after last keyframe
  if (time >= sorted[sorted.length - 1].time) {
    return sorted[sorted.length - 1].properties[propertyName] ?? defaultValue;
  }

  // Find the two keyframes to interpolate between
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];

    if (time >= start.time && time <= end.time) {
      const range = end.time - start.time;
      const progress = range === 0 ? 0 : (time - start.time) / range;

      const startVal = start.properties[propertyName];
      const endVal = end.properties[propertyName];

      if (typeof startVal === 'number' && typeof endVal === 'number') {
        return lerp(startVal, endVal, progress);
      }

      // For non-numeric properties, try to parse units (px, %, em, etc.)
      if (typeof startVal === 'string' && typeof endVal === 'string') {
        const startMatch = startVal.match(/^(-?[\d.]+)([a-z%]*)$/);
        const endMatch = endVal.match(/^(-?[\d.]+)([a-z%]*)$/);

        if (startMatch && endMatch && startMatch[2] === endMatch[2]) {
          const s = parseFloat(startMatch[1]);
          const e = parseFloat(endMatch[1]);
          const unit = startMatch[2];
          return `${lerp(s, e, progress)}${unit}`;
        }
      }

      // For non-interpolatable types, return startVal
      return startVal;
    }
  }

  return defaultValue;
};

/**
 * Converts a legacy "preset" animation into keyframes if needed
 */
export const getEffectiveKeyframes = (animation: AnimationSettings): Keyframe[] => {
  if (animation.keyframes && animation.keyframes.length > 0) {
    return animation.keyframes;
  }

  const { type, duration } = animation;

  switch (type as any) {
    // --- Basic Fades ---
    case 'fadeIn':
    case 'FadeIn':
    case 'fadein':
    case 'Fade':
    case 'fade':
      return [
        { time: 0, properties: { opacity: 0 } },
        { time: duration, properties: { opacity: 1 } }
      ];
    case 'fadeOut':
    case 'fadeout':
    case 'FadeOut':
      return [
        { time: 0, properties: { opacity: 1 } },
        { time: duration, properties: { opacity: 0 } }
      ];

    // --- Slides/Pans ---
    case 'slideIn':
    case 'SlideIn':
    case 'slidein':
    case 'Slide':
    case 'slide':
      return [
        { time: 0, properties: { x: -200, opacity: 0 } },
        { time: duration, properties: { x: 0, opacity: 1 } }
      ];
    case 'pan': // Text Pan
      return [
        { time: 0, properties: { x: -200, opacity: 0 } },
        { time: duration, properties: { x: 0, opacity: 1 } }
      ];
    case 'rise': // Text Rise Up
      return [
        { time: 0, properties: { y: 40, opacity: 0, scale: 0.95 } },
        { time: duration * 0.7, properties: { y: -5, opacity: 1, scale: 1.02 } },
        { time: duration, properties: { y: 0, opacity: 1, scale: 1 } }
      ];
    case 'drop': // Text Drop
      return [
        { time: 0, properties: { y: -40, opacity: 0, scale: 1.05 } },
        { time: duration * 0.7, properties: { y: 5, opacity: 1, scale: 0.98 } },
        { time: duration, properties: { y: 0, opacity: 1, scale: 1 } }
      ];

    // --- Scales/Zooms ---
    case 'scaleIn':
    case 'scalein':
    case 'scale':
    case 'zoomIn':
    case 'zoomin':
    case 'Zoom':
    case 'zoom':
    case 'Pop':
    case 'pop': // Text Pop
      return [
        { time: 0, properties: { scale: 0.5, opacity: 0, blurRadius: 4 } },
        { time: duration * 0.6, properties: { scale: 1.15, opacity: 1, blurRadius: 0 } },
        { time: duration * 0.8, properties: { scale: 0.95, opacity: 1 } },
        { time: duration, properties: { scale: 1, opacity: 1 } }
      ];
    case 'scaleOut':
    case 'scaleout':
      return [
        { time: 0, properties: { scale: 1, opacity: 1 } },
        { time: duration, properties: { scale: 0, opacity: 0 } }
      ];

    // --- Special Text Effects ---
    case 'typewriter':
      return [
        { time: 0, properties: { revealProgress: 0, opacity: 1 } },
        { time: duration, properties: { revealProgress: 100, opacity: 1 } }
      ];
    case 'blurIn':
    case 'Blur':
    case 'blur':
      return [
        { time: 0, properties: { blurRadius: 10, opacity: 0 } },
        { time: duration, properties: { blurRadius: 0, opacity: 1 } }
      ];
    case 'reveal': // Clip-path reveal
    case 'Reveal':
      return [
        { time: 0, properties: { revealProgress: 0, opacity: 0 } },
        { time: duration, properties: { revealProgress: 100, opacity: 1 } }
      ];
    case 'bounce':
    case 'Bounce':
      return [
        { time: 0, properties: { y: 0, scale: 0.3, opacity: 0 } },
        { time: duration * 0.3, properties: { y: 0, scale: 1.1, opacity: 1 } },
        { time: duration * 0.5, properties: { y: -30, scale: 0.9 } },
        { time: duration * 0.7, properties: { y: 0, scale: 1.05 } },
        { time: duration * 0.85, properties: { y: -10, scale: 0.98 } },
        { time: duration, properties: { y: 0, scale: 1, opacity: 1 } }
      ];
    case 'pulse':
      return [
        { time: 0, properties: { scale: 1, opacity: 1 } },
        { time: duration * 0.5, properties: { scale: 1.1, opacity: 0.7 } },
        { time: duration, properties: { scale: 1, opacity: 1 } }
      ];
    case 'wiggle':
      return [
        { time: 0, properties: { rotation: 0 } },
        { time: duration * 0.2, properties: { rotation: -8 } },
        { time: duration * 0.4, properties: { rotation: 6 } },
        { time: duration * 0.6, properties: { rotation: -4 } },
        { time: duration * 0.8, properties: { rotation: 2 } },
        { time: duration, properties: { rotation: 0 } }
      ];

    // --- Image/Shape ---
    case 'rotate':
    case 'Rotate':
      return [
        { time: 0, properties: { rotation: 0 } },
        { time: duration, properties: { rotation: 360 } }
      ];

    /*
    case 'parallax':
    case 'Parallax':
      return [
        { time: 0, properties: { x: 200, opacity: 0, scale: 1.05 } },
        { time: duration * 0.4, properties: { opacity: 1 } },
        { time: duration, properties: { x: 0, opacity: 1, scale: 1 } }
      ];
    */

    default:
      return [];
  }
};

/**
 * Calculates the minimum required duration for a template based on its animations.
 * Finds the max(delay + duration) across all layers and adds a 1s buffer.
 */
export const calculateMinTemplateDuration = (data: any): number => {
  let maxEndTime = 0;
  const layers = data.layers || data.dynamicLayers || [];

  const checkAnimation = (anim: any) => {
    if (anim && anim.type !== 'none') {
      const end = (Number(anim.delay) || 0) + (Number(anim.duration) || 1000);
      if (end > maxEndTime) maxEndTime = end;
    }
  };

  const traverse = (ls: any[]) => {
    if (!Array.isArray(ls)) return;
    ls.forEach(layer => {
      // Check animation in various places it might be stored
      checkAnimation(layer.animation);
      
      const id = layer.id;
      if (data.textStyles?.[id]?.animation) checkAnimation(data.textStyles[id].animation);
      if (data.imageSettings?.[id]?.animation) checkAnimation(data.imageSettings[id].animation);
      if (data.shapeSettings?.[id]?.animation) checkAnimation(data.shapeSettings[id].animation);
      if (data.videoSettings?.[id]?.animation) checkAnimation(data.videoSettings[id].animation);

      if (layer.children) traverse(layer.children);
    });
  };

  traverse(layers);
  
  // Template level animation
  if (data.templateSettings?.animation) checkAnimation(data.templateSettings.animation);

  return Math.max(2000, maxEndTime + 1000); // Minimum 2s
};

